import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, ArrowUpRight, ChevronDown } from "lucide-react";
import { useSite, tx } from "@/lib/lang";
import { ui } from "@/lib/uiStrings";
import { scrollToSection } from "@/hooks/useLenis";

export default function Nav() {
  const { content, lang, setLang } = useSite();
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [solOpen, setSolOpen] = useState(false);
  const [cliOpen, setCliOpen] = useState(false);
  const [chiOpen, setChiOpen] = useState(false);
  const [insOpen, setInsOpen] = useState(false);
  const t = ui[lang];
  const macroaree = content.macroaree || {};
  const targets = content.target || [];
  const [mobExp, setMobExp] = useState(null);
  const solItems = Object.entries(macroaree).map(([id, a]) => ({ id, to: `/soluzioni/${id}`, title: tx(a.title, lang) }));
  const cliItems = targets.map((seg) => ({ id: seg.id, to: `/clienti/${seg.id}`, title: tx(seg.label, lang) }));
  const chiItems = [
    { id: "chi-siamo", hash: "#chi-siamo", title: t.nav.chi, sub: t.nav.chiSub },
    { id: "manifesto", hash: "#manifesto", title: t.nav.manifesto, sub: t.nav.manifestoSub },
  ];
  const insItems = [
    { id: "osservatorio", to: "/osservatorio-energia", title: t.nav.ossTitle, sub: t.nav.ossSub },
    { id: "dati", hash: "#reporting", title: t.labels.reporting, sub: t.nav.datiSub },
  ];
  const links = [
    ["#chi-siamo", t.nav.chi],
    ["#servizi", t.nav.servizi],
    ["#target", t.nav.target],
    ["#metodo", t.nav.metodo],
    ["__insights", t.nav.insights],
  ];
  const go = (hash) => {
    setOpen(false);
    setSolOpen(false);
    setCliOpen(false);
    setChiOpen(false);
    setInsOpen(false);
    if (location.pathname !== "/") {
      navigate("/" + hash);
    } else {
      scrollToSection(hash);
    }
  };

  return (
    <header data-testid="main-nav" className="fixed top-0 inset-x-0 z-50 border-b border-ice/5 bg-onyx/80 backdrop-blur-md">
      <div className="mx-auto max-w-7xl px-6 h-[72px] flex items-center justify-between gap-4">
        <button data-testid="nav-logo" onClick={() => go("#hero")} className="shrink-0 hover:opacity-80 transition-opacity">
          <img src="/ethic-logo.png?v=6" alt="ETHIC ENERGY" className="h-7 w-auto" />
        </button>
        <nav className="hidden lg:flex items-center gap-7">
          {links.map(([hash, label]) => {
            if (hash === "__insights") {
              return (
                <div key={hash} className="relative" onMouseEnter={() => setInsOpen(true)} onMouseLeave={() => setInsOpen(false)}>
                  <button
                    data-testid="nav-link-insights"
                    onClick={() => setInsOpen(!insOpen)}
                    className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-ice transition-colors"
                  >
                    {label}
                    <ChevronDown size={13} className={`transition-transform ${insOpen ? "rotate-180" : ""}`} />
                  </button>
                  {insOpen && (
                    <div className="absolute top-full left-1/2 -translate-x-1/2 pt-3 z-50" data-testid="nav-insights-dropdown">
                      <div className="w-80 bg-surface border border-ice/10 rounded-xl p-2 shadow-2xl shadow-onyx">
                        {insItems.map((it) =>
                          it.to ? (
                            <Link
                              key={it.id}
                              data-testid={`nav-insights-${it.id}`}
                              to={it.to}
                              onClick={() => setInsOpen(false)}
                              className="block rounded-lg px-4 py-3 hover:bg-royal/15 transition-colors"
                            >
                              <span className="block text-sm font-semibold text-ice leading-snug">{it.title}</span>
                              <span className="block text-xs text-muted-foreground mt-0.5">{it.sub}</span>
                            </Link>
                          ) : (
                            <button
                              key={it.id}
                              data-testid={`nav-insights-${it.id}`}
                              onClick={() => go(it.hash)}
                              className="block w-full text-left rounded-lg px-4 py-3 hover:bg-royal/15 transition-colors"
                            >
                              <span className="block text-sm font-semibold text-ice leading-snug">{it.title}</span>
                              <span className="block text-xs text-muted-foreground mt-0.5">{it.sub}</span>
                            </button>
                          )
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            }
            if (hash === "#chi-siamo") {
              return (
                <div key={hash} className="relative" onMouseEnter={() => setChiOpen(true)} onMouseLeave={() => setChiOpen(false)}>
                  <button
                    data-testid="nav-link-chi-siamo"
                    onClick={() => go("#chi-siamo")}
                    className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-ice transition-colors"
                  >
                    {label}
                    <ChevronDown size={13} className={`transition-transform ${chiOpen ? "rotate-180" : ""}`} />
                  </button>
                  {chiOpen && (
                    <div className="absolute top-full left-1/2 -translate-x-1/2 pt-3 z-50" data-testid="nav-chisiamo-dropdown">
                      <div className="w-80 bg-surface border border-ice/10 rounded-xl p-2 shadow-2xl shadow-onyx">
                        {chiItems.map((it) => (
                          <button
                            key={it.id}
                            data-testid={`nav-chisiamo-${it.id}`}
                            onClick={() => go(it.hash)}
                            className="block w-full text-left rounded-lg px-4 py-3 hover:bg-royal/15 transition-colors"
                          >
                            <span className="block text-sm font-semibold text-ice leading-snug">{it.title}</span>
                            <span className="block text-xs text-muted-foreground mt-0.5">{it.sub}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            }
            if (hash === "#servizi" || hash === "#target") {
              const isSol = hash === "#servizi";
              const dropOpen = isSol ? solOpen : cliOpen;
              const setDropOpen = isSol ? setSolOpen : setCliOpen;
              const items = isSol
                ? Object.entries(macroaree).map(([id, a]) => ({ id, to: `/soluzioni/${id}`, title: tx(a.title, lang), sub: tx(a.payoff, lang) }))
                : targets.map((seg) => ({
                    id: seg.id,
                    to: `/clienti/${seg.id}`,
                    title: tx(seg.label, lang),
                    sub: tx(seg.headline, lang),
                  }));
              return (
                <div key={hash} className="relative" onMouseEnter={() => setDropOpen(true)} onMouseLeave={() => setDropOpen(false)}>
                  <button
                    data-testid={isSol ? "nav-link-servizi" : "nav-link-target"}
                    onClick={() => {
                      setDropOpen(false);
                      go(hash);
                    }}
                    className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-ice transition-colors"
                  >
                    {label}
                    <ChevronDown size={13} className={`transition-transform ${dropOpen ? "rotate-180" : ""}`} />
                  </button>
                  {dropOpen && (
                    <div className="absolute top-full left-1/2 -translate-x-1/2 pt-3 z-50" data-testid={isSol ? "nav-soluzioni-dropdown" : "nav-clienti-dropdown"}>
                      <div className="w-80 bg-surface border border-ice/10 rounded-xl p-2 shadow-2xl shadow-onyx">
                        {items.map((it) => (
                          <Link
                            key={it.id}
                            data-testid={isSol ? `nav-soluzioni-${it.id}` : `nav-clienti-${it.id}`}
                            to={it.to}
                            onClick={() => setDropOpen(false)}
                            className="block rounded-lg px-4 py-3 hover:bg-royal/15 transition-colors"
                          >
                            <span className="block text-sm font-semibold text-ice leading-snug">{it.title}</span>
                            <span className="block text-xs text-muted-foreground mt-0.5">{it.sub}</span>
                          </Link>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            }
            return (
              <button
                key={hash}
                data-testid={`nav-link-${hash.slice(1)}`}
                onClick={() => go(hash)}
                className="text-sm text-muted-foreground hover:text-ice transition-colors"
              >
                {label}
              </button>
            );
          })}
        </nav>
        <div className="flex items-center gap-3">
          <div className="hidden sm:flex items-center border border-ice/10 rounded-full p-0.5" data-testid="lang-toggle">
            {["it", "en"].map((l) => (
              <button
                key={l}
                data-testid={`lang-${l}`}
                onClick={() => setLang(l)}
                className={`px-2.5 py-1 rounded-full text-xs font-mono uppercase transition-colors ${
                  lang === l ? "bg-royal text-white" : "text-muted-foreground hover:text-ice"
                }`}
              >
                {l}
              </button>
            ))}
          </div>
          <button
            data-testid="nav-cta"
            onClick={() => go("#contatti")}
            className="hidden md:inline-flex items-center gap-1.5 bg-royal hover:bg-azure hover:text-[#0B1424] transition-colors text-white text-sm font-semibold px-4 py-2 rounded-full"
          >
            {t.nav.cta}
            <ArrowUpRight size={15} />
          </button>
          <button data-testid="nav-mobile-toggle" className="lg:hidden text-ice" onClick={() => setOpen(!open)}>
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>
      {open && (
        <div className="lg:hidden border-t border-ice/5 bg-onyx px-6 py-5 flex flex-col gap-4" data-testid="nav-mobile-menu">
          {links.map(([hash, label]) => {
            if (hash === "__insights") {
              return (
                <div key={hash}>
                  <div className="flex items-center justify-between">
                    <span data-testid="nav-mobile-link-insights" className="text-left text-ice/80 text-base">
                      {label}
                    </span>
                    <button
                      data-testid="nav-mobile-expand-insights"
                      onClick={() => setMobExp(mobExp === "insights" ? null : "insights")}
                      className="p-2 -mr-2 text-muted-foreground hover:text-ice transition-colors"
                      aria-label={`Espandi ${label}`}
                    >
                      <ChevronDown size={16} className={`transition-transform ${mobExp === "insights" ? "rotate-180" : ""}`} />
                    </button>
                  </div>
                  {mobExp === "insights" && (
                    <div className="mt-1 ml-2 pl-4 border-l border-royal/25 flex flex-col gap-3 py-1" data-testid="nav-mobile-insights-list">
                      {insItems.map((it) =>
                        it.to ? (
                          <Link
                            key={it.id}
                            data-testid={`nav-mobile-insights-${it.id}`}
                            to={it.to}
                            onClick={() => setOpen(false)}
                            className="text-sm text-muted-foreground hover:text-azure transition-colors"
                          >
                            {it.title}
                          </Link>
                        ) : (
                          <button
                            key={it.id}
                            data-testid={`nav-mobile-insights-${it.id}`}
                            onClick={() => go(it.hash)}
                            className="text-left text-sm text-muted-foreground hover:text-azure transition-colors"
                          >
                            {it.title}
                          </button>
                        )
                      )}
                    </div>
                  )}
                </div>
              );
            }
            if (hash === "#chi-siamo") {
              return (
                <div key={hash}>
                  <div className="flex items-center justify-between">
                    <button data-testid="nav-mobile-link-chi-siamo" onClick={() => go("#chi-siamo")} className="text-left text-ice/80 text-base">
                      {label}
                    </button>
                    <button
                      data-testid="nav-mobile-expand-chisiamo"
                      onClick={() => setMobExp(mobExp === "chisiamo" ? null : "chisiamo")}
                      className="p-2 -mr-2 text-muted-foreground hover:text-ice transition-colors"
                      aria-label={`Espandi ${label}`}
                    >
                      <ChevronDown size={16} className={`transition-transform ${mobExp === "chisiamo" ? "rotate-180" : ""}`} />
                    </button>
                  </div>
                  {mobExp === "chisiamo" && (
                    <div className="mt-1 ml-2 pl-4 border-l border-royal/25 flex flex-col gap-3 py-1" data-testid="nav-mobile-chisiamo-list">
                      {chiItems.map((it) => (
                        <button
                          key={it.id}
                          data-testid={`nav-mobile-chisiamo-${it.id}`}
                          onClick={() => go(it.hash)}
                          className="text-left text-sm text-muted-foreground hover:text-azure transition-colors"
                        >
                          {it.title}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              );
            }
            const dropKey = hash === "#servizi" ? "servizi" : hash === "#target" ? "target" : null;
            if (!dropKey) {
              return (
                <button key={hash} data-testid={`nav-mobile-link-${hash.slice(1)}`} onClick={() => go(hash)} className="text-left text-ice/80 text-base">
                  {label}
                </button>
              );
            }
            const items = dropKey === "servizi" ? solItems : cliItems;
            return (
              <div key={hash}>
                <div className="flex items-center justify-between">
                  <button data-testid={`nav-mobile-link-${hash.slice(1)}`} onClick={() => go(hash)} className="text-left text-ice/80 text-base">
                    {label}
                  </button>
                  <button
                    data-testid={`nav-mobile-expand-${dropKey}`}
                    onClick={() => setMobExp(mobExp === dropKey ? null : dropKey)}
                    className="p-2 -mr-2 text-muted-foreground hover:text-ice transition-colors"
                    aria-label={`Espandi ${label}`}
                  >
                    <ChevronDown size={16} className={`transition-transform ${mobExp === dropKey ? "rotate-180" : ""}`} />
                  </button>
                </div>
                {mobExp === dropKey && (
                  <div className="mt-1 ml-2 pl-4 border-l border-royal/25 flex flex-col gap-3 py-1" data-testid={`nav-mobile-${dropKey}-list`}>
                    {items.map((it) => (
                      <Link
                        key={it.id}
                        data-testid={`nav-mobile-${dropKey}-${it.id}`}
                        to={it.to}
                        onClick={() => setOpen(false)}
                        className="text-sm text-muted-foreground hover:text-azure transition-colors"
                      >
                        {it.title}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
          <div className="flex items-center gap-2 pt-2">
            {["it", "en"].map((l) => (
              <button
                key={l}
                data-testid={`lang-mobile-${l}`}
                onClick={() => setLang(l)}
                className={`px-3 py-1 rounded-full text-xs font-mono uppercase border ${lang === l ? "border-royal bg-royal text-white" : "border-ice/10 text-muted-foreground"}`}
              >
                {l}
              </button>
            ))}
          </div>
          <button data-testid="nav-mobile-cta" onClick={() => go("#contatti")} className="bg-royal text-white text-sm font-semibold px-4 py-2.5 rounded-full w-fit">
            {t.nav.cta}
          </button>
        </div>
      )}
    </header>
  );
}
